<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/Page-header";
import DocumentActions from "@/components/document/DocumentActions.vue";
import trainingAttendanceAPI from "@/services/standards/trainingProgram/trainingAttendanceAPI";
import Swal from "sweetalert2";

/**
 * 1.2.1 — Editor de asistencia por capacitación. Datos de la sesión + selección de colaboradores
 * activos que asistieron. Firma electrónica del encargado (DocumentActions); el PDF trae una línea
 * de firma en blanco por asistente para firma física.
 */
export default {
  page: { title: "Asistencia a capacitación" },
  components: { Layout, PageHeader, DocumentActions },
  data() {
    return {
      title: "1.2.1 Registro de asistencia a capacitación",
      items: [
        { text: "Estándares", href: "/forms/estandares" },
        {
          text: "1.2.1 Programa de capacitación",
          href: `/forms/estandares/${this.$store.getters["company/getCompany"].id || null}/promocion-prevencion`,
        },
        { text: "Asistencia", active: true },
      ],
      companyID: this.$store.getters["company/getCompany"].id || null,
      trainingId: this.$route.params.trainingId || null,
      form: {
        training_done: "",
        objective: "",
        session_date: null,
        place: "",
        modality: "Presencial",
        duration_hours: "",
        facilitator: "",
        directed_to: "",
        observations: "",
      },
      employees: [],
      selected: [],
      search: "",
      workflowStatus: "draft",
      signedAt: null,
      loading: true,
      submitting: false,
    };
  },
  computed: {
    isSigned() {
      return this.workflowStatus === "signed";
    },
    attendanceApi() {
      return {
        pdfUrl: (id) => trainingAttendanceAPI.pdfUrl(id),
        sign: (id, payload) => trainingAttendanceAPI.sign(id, payload),
      };
    },
    filteredEmployees() {
      const s = (this.search || "").toLowerCase().trim();
      if (!s) return this.employees;
      return this.employees.filter(
        (e) =>
          (e.full_name || "").toLowerCase().includes(s) ||
          String(e.identification_number || "").includes(s)
      );
    },
  },
  methods: {
    load() {
      trainingAttendanceAPI.employees(this.companyID)
        .then((r) => { this.employees = (r && r.data) ? r.data : []; });

      trainingAttendanceAPI.session(this.trainingId)
        .then((r) => {
          if (r) {
            Object.keys(this.form).forEach((k) => {
              if (r[k] !== undefined && r[k] !== null) this.form[k] = r[k];
            });
            if (r.session_date) this.form.session_date = r.session_date.substring(0, 10);
            this.workflowStatus = r.workflow_status || "draft";
            this.signedAt = r.signed_at || null;
            this.selected = (r.attendees || []).map((a) => a.employee_id).filter(Boolean);
          }
          this.loading = false;
        })
        .catch(() => { this.loading = false; });
    },
    isSelected(id) {
      return this.selected.includes(id);
    },
    toggle(id) {
      if (this.isSigned) return;
      const i = this.selected.indexOf(id);
      if (i === -1) this.selected.push(id);
      else this.selected.splice(i, 1);
    },
    save() {
      if (this.isSigned) return;
      this.submitting = true;
      const attendees = this.employees
        .filter((e) => this.selected.includes(e.id))
        .map((e) => ({
          employee_id: e.id,
          employee_nombre: e.full_name,
          employee_cedula: e.identification_number,
          employee_cargo: e.position,
          asistio: true,
        }));
      trainingAttendanceAPI.save(this.trainingId, { ...this.form, attendees })
        .then(() => {
          this.submitting = false;
          Swal.fire("¡Guardado!", "La asistencia se guardó correctamente.", "success");
        })
        .catch(() => {
          this.submitting = false;
          Swal.fire("Error", "No se pudo guardar la asistencia.", "error");
        });
    },
    onSigned(payload) {
      this.workflowStatus = payload.workflow_status;
      this.signedAt = payload.signed_at;
    },
    goBack() {
      this.$router.go(-1);
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row animated animatedFadeInUp fadeInUp">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-body">
            <div v-if="loading" class="text-center py-4"><b-spinner variant="primary" /></div>
            <template v-else>
              <div v-if="isSigned" class="alert alert-success py-2 small">
                Acta firmada. Los datos ya no se pueden editar.
              </div>

              <h5 class="text-primary">Datos de la capacitación</h5>
              <div class="form-row">
                <b-form-group class="col-md-8" label="Tema de la capacitación">
                  <b-form-input v-model="form.training_done" :disabled="isSigned" maxlength="60" />
                </b-form-group>
                <b-form-group class="col-md-4" label="Fecha de realización">
                  <b-form-input type="date" v-model="form.session_date" :disabled="isSigned" />
                </b-form-group>
                <b-form-group class="col-md-12" label="Objetivo">
                  <b-form-input v-model="form.objective" :disabled="isSigned" maxlength="300" />
                </b-form-group>
                <b-form-group class="col-md-4" label="Lugar">
                  <b-form-input v-model="form.place" :disabled="isSigned" maxlength="120" />
                </b-form-group>
                <b-form-group class="col-md-4" label="Modalidad">
                  <select class="form-control" v-model="form.modality" :disabled="isSigned">
                    <option>Presencial</option>
                    <option>Virtual</option>
                    <option>Mixta</option>
                  </select>
                </b-form-group>
                <b-form-group class="col-md-4" label="Intensidad horaria">
                  <b-form-input v-model="form.duration_hours" :disabled="isSigned" maxlength="20" placeholder="Ej: 2 horas" />
                </b-form-group>
                <b-form-group class="col-md-6" label="Facilitador / Capacitador">
                  <b-form-input v-model="form.facilitator" :disabled="isSigned" maxlength="150" />
                </b-form-group>
                <b-form-group class="col-md-6" label="Dirigido a">
                  <b-form-input v-model="form.directed_to" :disabled="isSigned" maxlength="150" placeholder="Ej: Todo el personal" />
                </b-form-group>
                <b-form-group class="col-md-12" label="Observaciones">
                  <b-form-textarea v-model="form.observations" :disabled="isSigned" rows="2" />
                </b-form-group>
              </div>

              <h5 class="text-primary mt-2">Asistentes ({{ selected.length }})</h5>
              <b-form-input v-model="search" size="sm" class="mb-2" placeholder="Buscar colaborador..." />
              <div class="table-responsive" style="max-height: 360px; overflow-y: auto;">
                <table class="table table-sm table-striped">
                  <thead>
                    <tr>
                      <th style="width:44px;"></th>
                      <th>Colaborador</th>
                      <th>Cédula</th>
                      <th>Cargo</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="e in filteredEmployees" :key="e.id" @click="toggle(e.id)" style="cursor:pointer;">
                      <td class="text-center"><input type="checkbox" :checked="isSelected(e.id)" :disabled="isSigned" @click.stop="toggle(e.id)" /></td>
                      <td>{{ e.full_name }}</td>
                      <td>{{ e.identification_number }}</td>
                      <td>{{ e.position }}</td>
                    </tr>
                    <tr v-if="!filteredEmployees.length">
                      <td colspan="4" class="text-center text-muted py-2">Sin colaboradores activos.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="text-right mt-3">
                <button class="btn btn-secondary mr-1" @click="goBack">Cancelar</button>
                <button class="btn btn-primary" @click="save" :disabled="isSigned || submitting">Guardar asistencia</button>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="col-lg-4" v-if="!loading">
        <h6 class="text-muted small mb-1">Acta de Asistencia</h6>
        <DocumentActions
          :document-id="Number(trainingId)"
          :company-id="companyID"
          :workflow-status="workflowStatus"
          :signed-at="signedAt"
          :api-service="attendanceApi"
          document-title="Registro de Asistencia a Capacitación"
          signer-role="gerente"
          @signed="onSigned"
        />
        <p class="text-muted small mt-2">
          Guarda la asistencia antes de firmar. Al firmar, el acta queda inmutable y la capacitación
          se marca como <strong>Ejecutada</strong>. Cada asistente firma físicamente sobre el PDF impreso.
        </p>
      </div>
    </div>
  </Layout>
</template>
